import type { ConsentSignatureT, ConsentViewT } from "@/types/database/consents";

export function compareConsentsBySortOrder(a: ConsentViewT, b: ConsentViewT): number {
    const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
    if (orderDiff !== 0) {
        return orderDiff;
    }
    return String(a.created_at ?? "").localeCompare(String(b.created_at ?? ""));
}

export function isConsentSignedByUser(consent: ConsentViewT, userId: string): boolean {
    const signatures = (consent.signatures ?? []) as ConsentSignatureT[];
    return signatures.some((signature) => signature.user_id === userId && Boolean(signature.signed_at));
}

/** True when this user explicitly declined the consent (signature slot withdrawn, never re-signed). */
export function isConsentDeclinedByUser(consent: ConsentViewT, userId: string): boolean {
    if (!consent.is_optional) {
        return false;
    }
    const signatures = (consent.signatures ?? []) as ConsentSignatureT[];
    return signatures.some(
        (signature) => signature.user_id === userId && !signature.signed_at && Boolean(signature.withdrawn_at)
    );
}

/** Signed or declined - a reviewed choice, so the consent step is complete. */
export function isConsentResolvedByUser(consent: ConsentViewT, userId: string): boolean {
    return isConsentSignedByUser(consent, userId) || isConsentDeclinedByUser(consent, userId);
}

/** True when this user has an unsigned signature slot on the consent. */
export function isUserAssignedToConsent(consent: ConsentViewT, userId: string): boolean {
    const signatures = (consent.signatures ?? []) as ConsentSignatureT[];
    return signatures.some((signature) => signature.user_id === userId);
}

export function getUnsignedConsents(consents: ConsentViewT[] | null | undefined, userId: string): ConsentViewT[] {
    if (!consents?.length || !userId) {
        return [];
    }

    return [...consents]
        .filter((consent) => isUserAssignedToConsent(consent, userId) && !isConsentResolvedByUser(consent, userId))
        .sort(compareConsentsBySortOrder);
}

export function getNextUnsignedConsent(
    consents: ConsentViewT[] | null | undefined,
    userId: string
): ConsentViewT | null {
    return getUnsignedConsents(consents, userId)[0] ?? null;
}

/** Required unresolved consents are the only consents that globally block diary routes. */
export function getBlockingRequiredConsents(
    consents: ConsentViewT[] | null | undefined,
    userId: string
): ConsentViewT[] {
    return getUnsignedConsents(consents, userId).filter((consent) => !consent.is_optional);
}

export function hasUnsignedConsents(consents: ConsentViewT[] | null | undefined, userId: string): boolean {
    return getUnsignedConsents(consents, userId).length > 0;
}

export function getConsentRedirectTarget(
    consents: ConsentViewT[] | null | undefined,
    userId: string | undefined
): { to: "/diaries/$diaryId/setup/consent/$consentId"; params: { diaryId: string; consentId: string } } | null {
    if (!userId) {
        return null;
    }

    const nextConsent = getBlockingRequiredConsents(consents, userId)[0] ?? null;
    if (!nextConsent?.consent_id || !nextConsent.diary_id) {
        return null;
    }

    return {
        to: "/diaries/$diaryId/setup/consent/$consentId",
        params: { diaryId: nextConsent.diary_id, consentId: nextConsent.consent_id },
    };
}
